<template>
  <main class="home" v-if="!loading && allowPhysicalCardActivation">
    <Header
      v-if="steps[0] != 'kyc'"
      :screen-title="t('PAYJOY_CARD_VIEWS.ACTIVATION_TITLE')"
      navbar-type="goBack"
      @clicked="router.back"
    />

    <IdentityVerification
      v-if="steps[0] == 'kyc'"
      @next="onCurpValidationSuccess"
      @on-validation-failed="onCurpValidationFailed"
      :require-validation-response="requiresIdentityResponse"
    />

    <AntiMoneyLaunderingSurvey
      class="mt-3"
      v-if="steps[0] == 'aml'"
      @on-continue-click="saveSurvey"
    />

    <CreditCardActivation
      v-if="steps.length == 0"
      :i-frame-url="iFrameUrl"
      :country="country"
    />
    <FullScreenModal
      :isOpen="isModalOpen"
      @onClose="closeResult"
      class="pt-5 pb-1"
      verticalAlignment="center"
    >
      <VerificationResult
        :icon="resultIcon"
        :title="resultTitle"
        :show-device-tag="resultError"
        :description="resultDescription"
        :btnText="resultBtnText"
        :error="resultError"
        @dismissFunc="resultDismissFunc"
        :custom-footer="resultError"
      >
        <div class="w-100 mt-auto mb-0">
          <Button
            id="wp-btn"
            class="text-center p-3 mb-3"
            @click="openWhatsApp()"
          >
            <p class="text-center text-white fw-bold my-auto">
              {{ t("PAYJOY_CARD_VIEWS.RESULT_SHIPPING_FORM.WHATSAPP") }}
            </p>
          </Button>
          <Button class="text-center p-3" @click="resultDismissFunc()">
            <p class="text-center text-white fw-bold my-auto">
              {{ resultBtnText }}
            </p>
          </Button>
        </div>
      </VerificationResult>
    </FullScreenModal>
  </main>
  <div v-if="!allowPhysicalCardActivation">
    <DownUpModal
      modal-height="45%"
      :modal-header-image="redWarningIcon"
      :modal-header-title="t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.TITLE')"
      :modal-header-subtitle="
        t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.DESCRIPTION')
      "
      :btn-text="t('GOHOME')"
      @modalAction="goHome"
    />
  </div>
  <LoadingOverlay :loading="loading" />
</template>

<script setup lang="ts">
import { onMounted, ref, watch, provide } from "vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import Header from "@/shared-components/consumer/Header.vue";
import CreditCardActivation from "@/apps/credit-line/components/CreditCard/Activation.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import IdentityVerification from "@/apps/credit-line/components/Withdrawal/IdentityVerification.vue";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useRouter } from "vue-router";
import FullScreenModal from "@/apps/credit-line/components/FullScreenModal.vue";
import VerificationResult from "@/apps/credit-line/components/Result.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import {
  AvailableFeatures,
  Country,
  FeatureControlService,
} from "@/apps/credit-line/services/feature-control.service";
import { AxiosError } from "axios";
import AntiMoneyLaunderingSurvey from "@/apps/credit-line/components/Withdrawal/AntiMoneyLaunderingSurvey.vue";
import { areValidationRequired } from "../../services/kyc";
import { KycUserFlow } from "../../enums/kycEnums";
import { IBaseResponse } from "../../models/IBaseResponse";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import { AMLSettings } from "@/apps/credit-line/helpers/aml-settings.helper.ts";

declare const Android: AndroidInterface;

const CreditLineService = new Service();
const { t } = useI18n();
const iFrameUrl = ref<string>();
const requiresIdentityResponse = ref();
const showKyc = ref<boolean>(false);
const loading = ref<boolean>(true);
const validationFailed = ref<boolean>(false);
const router = useRouter();
const FeatureControl = new FeatureControlService();
const { fetchSummary } = useCreditLineSummary();
//result
const isModalOpen = ref<boolean>(false);
const resultIcon = ref<string>();
const resultTitle = ref<string>();
const resultDescription = ref<string>();
const resultBtnText = ref<string>();
const resultError = ref<boolean>(false);
const userId = ref<number | undefined>();
const creditLineId = ref<number | undefined>();
const country = ref<Country>();
const amlSurvey = ref<boolean | undefined>(false);
const steps = ref<Array<string>>([]);
const isKeyboardOpen = ref<boolean>(false);
const allowPhysicalCardActivation = ref<boolean>(false);
const redWarningIcon = useAsset("img/credit-line/red_warning_icon.png");
const amlSettings = ref<AMLSettings | null>(null);

provide("amlSettings", amlSettings);

const openWhatsApp = () => {
  const helpMsg = t("CL_WP_HELP_MSG", {
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });
  window.open("https://wa.me/+525575028669?text=" + helpMsg, "_system");
};

const saveSurvey = (data) => {
  data.userId = userId.value;
  data.deviceTag = AuthenticationHelper.getDeviceTag();
  data.creditLineId = creditLineId.value;
  CreditLineService.SaveAmlSurvey(data)
    .then((res) => {
      if (res.data?.success) {
        // Only advance to next step if there's no blocking answer
        if (!data.hasBlockingAnswer) {
          steps.value.shift();
        }
      }
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
    });
};

const hideNav = () => {
  if (Android && typeof Android?.hideNav === "function") {
    Android.hideNav();
  }
};

watch(isKeyboardOpen, (newVal) => {
  if (!newVal) {
    hideNav();
  }
});

async function fetchKycValidations(creditLineIdParam: string) {
  try {
    const response = await areValidationRequired({
      creditLineId: Number(creditLineIdParam),
      userFlow: KycUserFlow.creditLineCardActivation,
      deviceTag: AuthenticationHelper.getDeviceTag(),
    });
    requiresIdentityResponse.value = response.data;
    if (response.data.wasSuccessful) {
      const areValidationsRequired =
        response.data.requiredValidations.length > 0;
      if (areValidationsRequired) {
        steps.value.push("kyc");
      }
      if (!areValidationsRequired) {
        showKyc.value = false;
        onCurpValidationSuccess();
      }
      return;
    }

    onCurpValidationFailed();
  } catch (error) {
    ErrorHandler.logCreditlineErrorResponse(error as IBaseResponse);
  }
}

const goHome = () => {
  safeAndroidCall("showNav");
  safeAndroidCall("dismissWebView");
};

async function getInitialData() {
  loading.value = true;
  /**
   * Edge case:
   * Super Pac-customers are exposed to a scenario where the app allows them to activate their
   * credit card, but the backend might be missing data to support the process. In order to avoid
   * blocking customers, we'll call the "createCardUser" endpoint. If we get a 200 response, then
   * we're ready for activation. If we get an explicit error code, then we'll need to request the
   * customer to confirm their personal information.
   */
  try {
    await CreditLineService.createCardUser({
      deviceTag: AuthenticationHelper.getDeviceTag(),
    });
  } catch (error) {
    const axiosError = error as AxiosError;
    ErrorHandler.handleNetworkErrorAndLog(axiosError);
    const controlledErrorCodes = [804, 805, 807, 808];
    const errorCode = axiosError?.response?.data?.errorCode;
    if (controlledErrorCodes.indexOf(errorCode) >= 0) {
      router.push({
        name: "CreditCardUserDataCaptureView",
        query: { type: "physical" },
      });
      return;
    }
  }
  try {
    return await fetchSummary();
  } catch (error) {
    return null;
  }
}
function onCurpValidationSuccess() {
  steps.value.shift();
  CreditLineService.activateCreditCard({
    deviceTag: AuthenticationHelper.getDeviceTag(),
  })
    .then((res) => {
      if (res.data.skipActivation) {
        router.replace({
          name: "CreditCardActivationResultView",
          query: {
            success: 1,
            iframe: encodeURI(res.data.iframeUrl),
          },
        });
      } else iFrameUrl.value = res.data.iframeUrl;
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
    });
}
function onCurpValidationFailed() {
  Android.finishedLoading();
  loading.value = false;
  validationFailed.value = true;
  resultIcon.value = useAsset("img/credit-line/error_warning_icon.svg");
  resultTitle.value = t("PAYJOY_CARD_VIEWS.KYC_VALIDATION_ERROR_TITLE");
  resultDescription.value = t(
    "PAYJOY_CARD_VIEWS.KYC_VALIDATION_ERROR_DESCRIPTION"
  );
  resultBtnText.value = t("PAYJOY_CARD_VIEWS.KYC_VALIDATION_ERROR_BTN");
  resultError.value = true;
  isModalOpen.value = true;
}
function closeResult() {
  Android.dismissWebView();
}
function resultDismissFunc() {
  router.replace({ name: "CreditLineHelpView" });
  return;
}
async function requireAmlSurvey(summary) {
  CreditLineService.RequireAmlSurvey({
    deviceTag: AuthenticationHelper.getDeviceTag(),
    creditLineId: summary.creditLineId as number,
    userId: summary.userId,
  })
    .then((res) => {
      amlSurvey.value = res.data?.needSurvey && summary.withdrawals.length == 0;
      steps.value.push("aml");
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
    });
}

onMounted(async () => {
  const cardActivationSettings = await getSettings(
    SettingsCategory.CL_CARDS_PAGE
  );

  allowPhysicalCardActivation.value =
    cardActivationSettings.allowPhysicalCardActivation?.enabled || false;

  amlSettings.value = cardActivationSettings?.amlSurveyConfiguration?.values
    ?.formValuesRules as unknown as AMLSettings | null;

  if (!allowPhysicalCardActivation.value) {
    safeAndroidCall("hideNav");
  }

  const summary = await getInitialData();

  hideNav();
  const handleResize = () => {
    isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
  };
  window.addEventListener("resize", handleResize);
  handleResize();

  country.value = summary?.country?.toUpperCase() as Country;
  userId.value = summary?.userId;
  creditLineId.value = summary?.creditLineId as number;

  let amlOn = false;
  if (summary?.creditLineId) {
    const kycOn = FeatureControl.isFeatureEnabled(
      AvailableFeatures.KYC,
      summary.country as Country,
      summary.customerType
    );

    amlOn = FeatureControl.isFeatureEnabled(
      AvailableFeatures.AML_SURVEY,
      summary.country as Country
    );

    // Check AML survey requirements first to ensure sequential API calls
    if (amlOn) {
      await requireAmlSurvey(summary);
    }

    // Then proceed with KYC validation or activation
    if (kycOn) {
      await fetchKycValidations(`${summary?.creditLineId}`);
    } else if (!amlSurvey.value) {
      // Only activate if no AML survey is required
      onCurpValidationSuccess();
    }
  }

  if (isAndroid()) {
    Android.finishedLoading();
  }
  loading.value = false;
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
</style>
